import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.js';
import FunnelReadinessPanel from '../components/FunnelReadinessPanel.jsx';
import ProtectedPageLoader from '../components/ProtectedPageLoader.jsx';
import {
  ActionFormDialog,
  useActionFormDialog,
} from '../components/ActionFormDialog.jsx';
import { Button, Pill } from '../components/primitives.jsx';
import { PALETTE } from '../palette.js';
import { refreshSaasBrandResearch } from '../saas-brand-research.js';

function activeCompanyId(session) {
  const memberships = session?.user?.memberships || [];
  return session?.user?.activeCompany?.id || memberships[0]?.companyId || '';
}

function activeRole(session) {
  const companyId = activeCompanyId(session);
  return session?.user?.memberships?.find((membership) => membership.companyId === companyId)?.role
    || session?.user?.activeCompany?.role
    || '';
}

function tenantHeaders(companyId, headers = {}) {
  return { headers: { 'x-company-id': companyId, ...headers } };
}

function lifecycleTone(value) {
  if (value === 'published' || value === 'approved') return 'live';
  if (value === 'failed') return 'warn';
  if (value === 'draft') return 'accent';
  return 'neutral';
}

function integrityTone(value) {
  if (value === 'healthy') return 'live';
  if (value === 'warning' || value === 'blocked') return 'warn';
  return 'neutral';
}

function labelize(value, fallback = 'Not available') {
  const text = typeof value === 'string' && value ? value : fallback;
  return text.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

function safeMessage(error, fallback) {
  const code = String(error?.code || '').toLowerCase();
  if (code === 'funnel_not_found' || code === 'funnel_version_not_found') {
    return 'This funnel version is not available in the active company.';
  }
  if (code === 'funnel_version_not_approved') return 'Approve this exact version before publishing it.';
  if (code === 'funnel_platform_domain_provisioning_pending') return 'The platform URL is still provisioning. Try publishing again shortly.';
  if (Number(error?.status) === 403) return 'Your company role cannot perform this action.';
  return fallback;
}

function versionRoute(funnelId, versionId) {
  const query = new URLSearchParams({ funnel: funnelId });
  if (versionId) query.set('version', versionId);
  return `#/funnels?${query.toString()}`;
}

function changedFields(fields, draft) {
  return fields
    .filter((field) => String(draft[field.id] ?? field.value).trim() !== String(field.value).trim())
    .map((field) => ({ fieldId: field.id, value: String(draft[field.id] ?? '').trim() }));
}

const PREVIEW_CHANNEL_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function previewEmbedUrl(url, channel) {
  if (!PREVIEW_CHANNEL_PATTERN.test(String(channel || ''))) return '';
  try {
    const parsed = new URL(String(url || ''));
    if (parsed.protocol !== 'https:'
      && !(parsed.protocol === 'http:' && ['127.0.0.1', 'localhost'].includes(parsed.hostname))) return '';
    parsed.hash = new URLSearchParams({ bl_preview_channel: channel }).toString();
    return parsed.toString();
  } catch {
    return '';
  }
}

export function isPreviewReadyMessage(event, frameWindow, channel) {
  const data = event?.data;
  return event?.source === frameWindow
    && data && typeof data === 'object' && !Array.isArray(data)
    && Object.keys(data).sort().join(',') === 'channel,type'
    && data.type === 'beyondleads.funnel.preview.ready'
    && data.channel === channel;
}

const styles = `
  .funnel-workbench-page {
    max-width: 1560px;
    margin: 0 auto;
    padding: 28px 32px 56px;
    font-family: Inter, sans-serif;
  }
  .funnel-workbench-toolbar {
    display: flex;
    align-items: flex-end;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 22px;
  }
  .funnel-workbench-grid {
    display: grid;
    grid-template-columns: 230px minmax(420px, 1fr) minmax(290px, 360px);
    gap: 18px;
    align-items: start;
  }
  .funnel-workbench-panel {
    border: 1px solid ${PALETTE.line};
    border-radius: 8px;
    background: ${PALETTE.surface};
    overflow: hidden;
  }
  .funnel-version-list {
    max-height: 680px;
    overflow-y: auto;
  }
  .funnel-preview-shell {
    min-height: 680px;
    display: flex;
    flex-direction: column;
  }
  .funnel-preview-viewport {
    flex: 1;
    min-height: 600px;
    background: ${PALETTE.surfaceAlt};
    display: flex;
    justify-content: center;
    position: relative;
    padding: 12px;
  }
  .funnel-preview-viewport iframe {
    width: 100%;
    min-height: 574px;
    border: 0;
    background: white;
  }
  .funnel-preview-viewport.mobile iframe {
    width: min(390px, 100%);
    border: 1px solid ${PALETTE.line};
  }
  .funnel-preview-viewport iframe.funnel-preview-frame-pending {
    position: absolute;
    inset: 12px;
    width: calc(100% - 24px);
    visibility: hidden;
    pointer-events: none;
  }
  .funnel-inspector-body {
    max-height: 680px;
    overflow-y: auto;
  }
  @media (max-width: 1180px) {
    .funnel-workbench-grid {
      grid-template-columns: 210px minmax(0, 1fr);
    }
    .funnel-workbench-inspector {
      grid-column: 1 / -1;
    }
    .funnel-inspector-body {
      max-height: none;
    }
  }
  @media (max-width: 760px) {
    .funnel-workbench-page { padding: 22px 16px 44px; }
    .funnel-workbench-grid { grid-template-columns: minmax(0, 1fr); }
    .funnel-version-list { max-height: none; }
    .funnel-preview-shell { min-height: 560px; }
    .funnel-preview-viewport { min-height: 500px; padding: 8px; }
    .funnel-preview-viewport iframe { min-height: 480px; }
    .funnel-preview-viewport iframe.funnel-preview-frame-pending { inset: 8px; width: calc(100% - 16px); }
    .funnel-workbench-inspector { grid-column: auto; }
  }
`;

export default function SaasFunnelWorkbench({ navigate, route, session, previewSandbox }) {
  const companyId = activeCompanyId(session);
  const role = activeRole(session);
  const canManage = ['owner', 'admin'].includes(role);
  const [brands, setBrands] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [funnels, setFunnels] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [workbench, setWorkbench] = useState(null);
  const [workbenchKey, setWorkbenchKey] = useState('');
  const [preview, setPreview] = useState({
    status: 'idle',
    url: '',
    message: '',
    channel: '',
    frameStatus: 'idle',
  });
  const previewFrameRef = useRef(null);
  const previewChannelRef = useRef('');
  const previewTimeoutRef = useRef(null);
  const [viewport, setViewport] = useState('desktop');
  const [draft, setDraft] = useState({});
  const [editGroup, setEditGroup] = useState('');
  const [action, setAction] = useState({ status: 'idle', message: '' });
  const [completionAction, setCompletionAction] = useState({ status: 'idle', message: '' });
  const [listAction, setListAction] = useState({ status: 'idle', message: '' });
  const [error, setError] = useState('');
  const funnelId = route?.query?.funnel || '';
  const versionId = route?.query?.version || '';
  const companyRef = useRef(companyId);
  const funnelRef = useRef(funnelId);
  const canManageRef = useRef(canManage);
  const workbenchRef = useRef(workbench);
  const actionStatusRef = useRef(action.status);
  const publishInFlightRef = useRef(false);
  const publishOperationRef = useRef(0);
  const actionFormDialog = useActionFormDialog();
  companyRef.current = companyId;
  funnelRef.current = funnelId;
  canManageRef.current = canManage;
  workbenchRef.current = workbench;
  actionStatusRef.current = action.status;

  useEffect(() => {
    let active = true;
    setBrands(null);
    setFunnels(null);
    setWorkbench(null);
    setWorkbenchKey('');
    setSelectedBrand('');
    setError('');
    if (!companyId) return () => { active = false; };
    api.get('/api/saas/brands', tenantHeaders(companyId))
      .then((response) => {
        if (!active) return;
        const next = response.brands || [];
        setBrands(next);
        setSelectedBrand(next[0]?.id || '');
      })
      .catch((requestError) => {
        if (!active) return;
        setBrands([]);
        setError(safeMessage(requestError, 'Brands could not be loaded. Try again.'));
      });
    return () => { active = false; };
  }, [companyId]);

  useEffect(() => {
    let active = true;
    if (!companyId || !selectedBrand || funnelId) return () => { active = false; };
    setFunnels(null);
    setListAction({ status: 'idle', message: '' });
    setError('');
    api.get(
      `/api/saas/funnels?brandId=${encodeURIComponent(selectedBrand)}&limit=50&offset=0`,
      tenantHeaders(companyId),
    ).then((response) => {
      if (!active) return;
      setFunnels(response.funnels || []);
      setPagination(response.pagination || null);
    }).catch((requestError) => {
      if (!active) return;
      setFunnels([]);
      setError(safeMessage(requestError, 'Funnels could not be loaded. Try again.'));
    });
    return () => { active = false; };
  }, [companyId, selectedBrand, funnelId]);

  useEffect(() => {
    let active = true;
    publishOperationRef.current += 1;
    publishInFlightRef.current = false;
    actionStatusRef.current = 'idle';
    if (!companyId || !funnelId) return () => { active = false; };
    const key = `${companyId}:${funnelId}:${versionId || 'latest'}`;
    setWorkbench(null);
    setWorkbenchKey('');
    previewChannelRef.current = '';
    setPreview({ status: 'idle', url: '', message: '', channel: '', frameStatus: 'idle' });
    setAction({ status: 'idle', message: '' });
    setCompletionAction({ status: 'idle', message: '' });
    setError('');
    const versionQuery = versionId ? `?versionId=${encodeURIComponent(versionId)}` : '';
    api.get(`/api/saas/funnels/${encodeURIComponent(funnelId)}${versionQuery}`, tenantHeaders(companyId))
      .then((response) => {
        if (!active) return;
        setWorkbench(response);
        setWorkbenchKey(key);
      })
      .catch((requestError) => {
        if (!active) return;
        setWorkbenchKey(key);
        setError(safeMessage(requestError, 'The funnel workbench could not be loaded.'));
      });
    return () => { active = false; };
  }, [companyId, funnelId, versionId]);

  useEffect(() => {
    const fields = workbench?.editableFields || [];
    setDraft(Object.fromEntries(fields.map((field) => [field.id, field.value])));
    setEditGroup(fields[0]?.group || '');
  }, [workbench?.selectedVersion?.id]);

  useEffect(() => {
    let active = true;
    const selectedVersionId = workbench?.selectedVersion?.id;
    if (!companyId || !funnelId || !selectedVersionId) return () => { active = false; };
    setPreview({
      status: 'loading',
      url: '',
      message: 'Authorizing exact-version preview...',
      channel: '',
      frameStatus: 'idle',
    });
    api.post(
      `/api/saas/funnels/${encodeURIComponent(funnelId)}/previews`,
      { versionId: selectedVersionId, environment: 'staging', expiresInSeconds: 900 },
      tenantHeaders(companyId),
    ).then((response) => {
      if (!active) return;
      const url = response.preview?.url || '';
      const channel = url ? crypto.randomUUID() : '';
      previewChannelRef.current = channel;
      setPreview(url ? {
        status: 'ready',
        url,
        message: '',
        channel,
        frameStatus: 'loading',
      } : {
        status: 'error',
        url: '',
        message: 'This version could not be previewed.',
        channel: '',
        frameStatus: 'idle',
      });
    }).catch((requestError) => {
      if (!active) return;
      previewChannelRef.current = '';
      setPreview({
        status: 'error',
        url: '',
        message: safeMessage(requestError, 'This version could not be previewed.'),
        channel: '',
        frameStatus: 'idle',
      });
    });
    return () => { active = false; };
  }, [companyId, funnelId, workbench?.selectedVersion?.id]);

  useEffect(() => {
    function handleMessage(event) {
      const channel = previewChannelRef.current;
      if (!isPreviewReadyMessage(event, previewFrameRef.current?.contentWindow, channel)) return;
      window.clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
      setPreview((current) => current.channel === channel ? {
        ...current,
        frameStatus: 'ready',
        message: '',
      } : current);
    }
    window.addEventListener('message', handleMessage);
    return () => {
      window.clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    if (preview.status !== 'ready' || preview.frameStatus !== 'loading' || !preview.channel) return undefined;
    const channel = preview.channel;
    window.clearTimeout(previewTimeoutRef.current);
    previewTimeoutRef.current = window.setTimeout(() => {
      previewTimeoutRef.current = null;
      setPreview((current) => current.channel === channel ? {
        ...current,
        frameStatus: 'error',
        message: 'The embedded preview could not be displayed. Open it in a new tab or try again.',
      } : current);
    }, 10_000);
    return () => {
      window.clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    };
  }, [preview.status, preview.frameStatus, preview.channel]);

  const groups = useMemo(
    () => [...new Set((workbench?.editableFields || []).map((field) => field.group))],
    [workbench?.editableFields],
  );
  const visibleFields = (workbench?.editableFields || []).filter((field) => field.group === editGroup);
  const changes = changedFields(workbench?.editableFields || [], draft);
  const selectedVersion = workbench?.selectedVersion;
  const stagingPublication = workbench?.publications?.staging;
  const isSelectedPublished = Boolean(
    stagingPublication
    && ['publish', 'rollback'].includes(stagingPublication.action)
    && stagingPublication.versionId === selectedVersion?.id,
  );

  async function loadMore() {
    if (!pagination?.hasMore || !selectedBrand || listAction.status === 'working') return;
    setListAction({ status: 'working', message: '' });
    try {
      const response = await api.get(
        `/api/saas/funnels?brandId=${encodeURIComponent(selectedBrand)}&limit=50&offset=${pagination.nextOffset}`,
        tenantHeaders(companyId),
      );
      setFunnels((current) => [...new Map([...(current || []), ...(response.funnels || [])]
        .map((funnel) => [funnel.id, funnel])).values()]);
      setPagination(response.pagination || null);
      setListAction({ status: 'idle', message: '' });
    } catch (requestError) {
      setListAction({
        status: 'error',
        message: safeMessage(requestError, 'More funnels could not be loaded. Try again.'),
      });
    }
  }

  async function saveVersion() {
    if (!canManage || !selectedVersion?.id || changes.length === 0 || action.status === 'working') return;
    setAction({ status: 'working', message: 'Validating and saving a new immutable version...' });
    try {
      const idempotencyKey = `funnel-definition-edit:${crypto.randomUUID()}`;
      const response = await api.post(
        `/api/saas/funnels/${encodeURIComponent(funnelId)}/edits`,
        { baseVersionId: selectedVersion.id, changes, idempotencyKey },
        tenantHeaders(companyId, { 'Idempotency-Key': idempotencyKey }),
      );
      setWorkbench(response);
      setWorkbenchKey(`${companyId}:${funnelId}:${response.selectedVersion?.id}`);
      window.location.hash = versionRoute(funnelId, response.selectedVersion?.id).slice(1);
      setAction({ status: 'done', message: 'New draft version saved. The live version did not change.' });
    } catch (requestError) {
      setAction({ status: 'error', message: safeMessage(requestError, 'The new version could not be saved.') });
    }
  }

  async function completeFunnel(submission) {
    if (!canManage || !selectedVersion?.id || completionAction.status === 'working') return;
    const idempotencyKey = `funnel-review-completion:${crypto.randomUUID()}`;
    setCompletionAction({
      status: 'working',
      message: 'Confirming review values and creating a new immutable version...',
    });
    try {
      const response = await api.post(
        `/api/saas/funnels/${encodeURIComponent(funnelId)}/completion`,
        { ...submission, idempotencyKey },
        tenantHeaders(companyId, { 'Idempotency-Key': idempotencyKey }),
      );
      setWorkbench(response);
      setWorkbenchKey(`${companyId}:${funnelId}:${response.selectedVersion?.id}`);
      window.location.hash = versionRoute(funnelId, response.selectedVersion?.id).slice(1);
      setCompletionAction({
        status: 'done',
        message: response.completion?.readiness?.status === 'ready'
          ? 'Confirmed values saved. This exact version is ready for approval.'
          : 'Confirmed values saved in a new version. Resolve the remaining required items before approval.',
      });
    } catch (requestError) {
      setCompletionAction({
        status: 'error',
        message: safeMessage(requestError, 'The confirmed funnel values could not be saved.'),
      });
    }
  }

  async function refreshResearch() {
    const brand = workbench?.funnel?.brand;
    if (!canManage || !brand?.id || !brand.websiteUrl || completionAction.status === 'working') return;
    setCompletionAction({ status: 'working', message: 'Refreshing website research candidates...' });
    try {
      await refreshSaasBrandResearch({
        companyId,
        brandId: brand.id,
        url: brand.websiteUrl,
        onProgress: (event) => setCompletionAction({
          status: 'working',
          message: event.label || 'Refreshing website research candidates...',
        }),
      });
      const versionQuery = selectedVersion?.id
        ? `?versionId=${encodeURIComponent(selectedVersion.id)}`
        : '';
      const response = await api.get(
        `/api/saas/funnels/${encodeURIComponent(funnelId)}${versionQuery}`,
        tenantHeaders(companyId),
      );
      setWorkbench(response);
      setCompletionAction({
        status: 'done',
        message: 'Website candidates refreshed. Review and explicitly confirm any values you want to use.',
      });
    } catch (requestError) {
      setCompletionAction({
        status: 'error',
        message: safeMessage(requestError, 'Website research could not be refreshed.'),
      });
    }
  }

  async function checkLinkIntegrity() {
    if (!canManage || !selectedVersion?.id || action.status === 'working') return;
    setAction({ status: 'working', message: 'Checking external links and booking readiness...' });
    try {
      const response = await api.post(
        `/api/saas/funnels/${encodeURIComponent(funnelId)}/link-integrity-checks`,
        { versionId: selectedVersion.id, environment: 'staging' },
        tenantHeaders(companyId),
      );
      setWorkbench((current) => ({
        ...current,
        linkIntegrity: {
          ...(current?.linkIntegrity || {}),
          prePublish: response.linkIntegrity,
        },
      }));
      setAction({
        status: 'done',
        message: response.linkIntegrity?.status === 'healthy'
          ? 'Links and booking readiness passed.'
          : response.linkIntegrity?.status === 'warning'
            ? 'Readiness check completed with warnings.'
            : 'Broken links must be corrected or explicitly overridden before publish.',
      });
    } catch (requestError) {
      setAction({ status: 'error', message: safeMessage(requestError, 'Link readiness could not be checked.') });
    }
  }

  async function publishVersion({ override = false } = {}) {
    const requestedCompanyId = companyId;
    const requestedFunnelId = funnelId;
    const requestedVersionId = selectedVersion?.id;
    const requestedOperationSequence = publishOperationRef.current;
    if (
      !canManage
      || !requestedCompanyId
      || !requestedFunnelId
      || !requestedVersionId
      || actionStatusRef.current === 'working'
      || publishInFlightRef.current
    ) return;
    let overrideReason = null;
    if (override) {
      const fields = await actionFormDialog.request({
        eyebrow: 'Staging publication override',
        title: 'Publish with broken external links?',
        description: 'This publishes the exact approved funnel version to staging while its external-link readiness is blocked. Record the reviewed reason for the audit trail.',
        submitLabel: 'Publish with override',
        cancelLabel: 'Keep unpublished',
        tone: 'warning',
        initialFocus: 'cancel',
        fields: [{
          name: 'reason',
          label: 'Override reason',
          value: 'Owner reviewed link readiness and accepts the staging risk',
          multiline: true,
          required: true,
          help: 'Use tenant-safe operational detail only. Do not include credentials or secret values.',
        }],
      });
      if (!fields) return;
      overrideReason = fields.reason;
    }
    const currentWorkbench = workbenchRef.current;
    const currentPublication = currentWorkbench?.publications?.staging;
    const alreadyPublished = Boolean(
      currentPublication
      && ['publish', 'rollback'].includes(currentPublication.action)
      && currentPublication.versionId === requestedVersionId
    );
    if (
      companyRef.current !== requestedCompanyId
      || funnelRef.current !== requestedFunnelId
      || currentWorkbench?.selectedVersion?.id !== requestedVersionId
      || publishOperationRef.current !== requestedOperationSequence
      || !canManageRef.current
      || actionStatusRef.current === 'working'
      || publishInFlightRef.current
      || currentWorkbench?.selectedVersion?.approvalStatus !== 'approved'
      || alreadyPublished
      || (override && currentWorkbench?.linkIntegrity?.prePublish?.status !== 'blocked')
      || (!override && currentWorkbench?.linkIntegrity?.prePublish?.status === 'blocked')
    ) return;
    const operationId = ++publishOperationRef.current;
    publishInFlightRef.current = true;
    actionStatusRef.current = 'working';
    setAction({ status: 'working', message: 'Publishing this exact version to staging...' });
    try {
      const idempotencyKey = `funnel-workbench-publish:${crypto.randomUUID()}`;
      await api.post(
        `/api/saas/funnels/${encodeURIComponent(requestedFunnelId)}/publications`,
        {
          versionId: requestedVersionId,
          environment: 'staging',
          reason: 'funnel_workbench_explicit_publish',
          idempotencyKey,
          overrideLinkIntegrity: override,
          overrideReason,
        },
        tenantHeaders(requestedCompanyId, { 'Idempotency-Key': idempotencyKey }),
      );
      if (
        companyRef.current !== requestedCompanyId
        || funnelRef.current !== requestedFunnelId
        || workbenchRef.current?.selectedVersion?.id !== requestedVersionId
        || publishOperationRef.current !== operationId
      ) return;
      const response = await api.get(
        `/api/saas/funnels/${encodeURIComponent(requestedFunnelId)}?versionId=${encodeURIComponent(requestedVersionId)}`,
        tenantHeaders(requestedCompanyId),
      );
      if (
        companyRef.current !== requestedCompanyId
        || funnelRef.current !== requestedFunnelId
        || workbenchRef.current?.selectedVersion?.id !== requestedVersionId
        || publishOperationRef.current !== operationId
      ) return;
      setWorkbench(response);
      setAction({ status: 'done', message: 'Staging publication updated to this version.' });
    } catch (requestError) {
      if (
        companyRef.current === requestedCompanyId
        && funnelRef.current === requestedFunnelId
        && publishOperationRef.current === operationId
      ) {
        setAction({ status: 'error', message: safeMessage(requestError, 'This version could not be published.') });
      }
    } finally {
      if (publishOperationRef.current === operationId) {
        publishInFlightRef.current = false;
      }
      if (
        companyRef.current === requestedCompanyId
        && funnelRef.current === requestedFunnelId
        && publishOperationRef.current === operationId
      ) {
        actionStatusRef.current = 'idle';
      }
    }
  }

  if (!companyId || brands == null || (funnelId && workbenchKey !== `${companyId}:${funnelId}:${versionId || 'latest'}`)) {
    return (
      <div className="funnel-workbench-page">
        <style>{styles}</style>
        <ProtectedPageLoader
          title="Loading funnel workbench"
          body="Resolving company ownership, immutable versions, and publication state."
          minHeight={420}
        />
      </div>
    );
  }

  if (!funnelId) {
    return (
      <div className="funnel-workbench-page">
        <style>{styles}</style>
        <header className="funnel-workbench-toolbar">
          <div>
            <div style={eyebrowStyle}>Versioned experiences</div>
            <h1 style={pageTitleStyle}>Funnel workbench</h1>
            <p style={subtitleStyle}>Review drafts, exact previews, approvals, and live staging publications.</p>
          </div>
          <div style={{ flex: 1 }} />
          <Button variant="accent" onClick={() => navigate('wizard')}>New campaign</Button>
        </header>
        {error && <Notice tone="error">{error}</Notice>}
        <section style={{ marginBottom: 24 }}>
          <label htmlFor="funnel-brand" style={fieldLabelStyle}>Brand</label>
          <select
            id="funnel-brand"
            value={selectedBrand}
            onChange={(event) => setSelectedBrand(event.target.value)}
            style={{ ...inputStyle, maxWidth: 420 }}
          >
            {(brands || []).map((brand) => <option key={brand.id} value={brand.id}>{brand.name}</option>)}
          </select>
        </section>
        {brands.length === 0 ? (
          <Empty title="No brands yet" body="Complete company onboarding before creating a campaign funnel." />
        ) : funnels == null ? (
          <ProtectedPageLoader title="Loading funnels" body="Reading tenant-owned logical funnels." minHeight={300} />
        ) : funnels.length === 0 ? (
          <Empty title="No funnels for this brand" body="Run a campaign workflow to create the first immutable funnel version." />
        ) : (
          <div style={{ borderTop: `1px solid ${PALETTE.line}` }}>
            {funnels.map((funnel) => (
              <a key={funnel.id} href={versionRoute(funnel.id)} style={funnelRowStyle}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 650, color: PALETTE.ink, marginBottom: 5 }}>{funnel.name}</div>
                  <div style={{ fontSize: 12, color: PALETTE.ink3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {funnel.campaign?.name || funnel.slug}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {funnel.latestVersion ? <span style={monoStyle}>v{funnel.latestVersion.versionNumber}</span> : null}
                  <Pill tone={lifecycleTone(funnel.latestVersion?.lifecycle)}>
                    {labelize(funnel.latestVersion?.lifecycle, 'Awaiting version')}
                  </Pill>
                  <span aria-hidden="true" style={{ color: PALETTE.ink3 }}>&gt;</span>
                </div>
              </a>
            ))}
          </div>
        )}
        {pagination?.hasMore && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
            <Button variant="soft" onClick={loadMore} disabled={listAction.status === 'working'}>
              {listAction.status === 'working' ? 'Loading...' : 'Load more'}
            </Button>
          </div>
        )}
        {listAction.message && <Notice tone="error">{listAction.message}</Notice>}
      </div>
    );
  }

  if (!workbench) {
    return (
      <div className="funnel-workbench-page">
        <style>{styles}</style>
        <button type="button" onClick={() => navigate('funnels')} style={backButtonStyle}>Back to funnels</button>
        <Notice tone="error">{error || 'The funnel workbench could not be loaded.'}</Notice>
      </div>
    );
  }

  return (
    <div className="funnel-workbench-page">
      <style>{styles}</style>
      <header className="funnel-workbench-toolbar">
        <div style={{ minWidth: 0 }}>
          <button type="button" onClick={() => navigate('funnels')} style={backButtonStyle}>Back to funnels</button>
          <div style={eyebrowStyle}>{workbench.funnel.brand?.name} / {workbench.funnel.campaign?.name}</div>
          <h1 style={{ ...pageTitleStyle, fontSize: 42 }}>{workbench.funnel.name}</h1>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Pill tone={lifecycleTone(selectedVersion?.lifecycle)}>{labelize(selectedVersion?.lifecycle)}</Pill>
          {workbench.activeUrl && (
            <Button variant="ghost" size="sm" onClick={() => window.open(workbench.activeUrl, '_blank', 'noopener')}>Open live site</Button>
          )}
        </div>
      </header>
      {error && <Notice tone="error">{error}</Notice>}
      {action.message && <Notice tone={action.status === 'error' ? 'error' : 'info'}>{action.message}</Notice>}

      <FunnelReadinessPanel
        completion={workbench.completion}
        selectedVersionId={selectedVersion?.id}
        canManage={canManage}
        busy={completionAction.status === 'working'}
        message={completionAction.message}
        onComplete={completeFunnel}
        onRefreshResearch={refreshResearch}
      />

      <div className="funnel-workbench-grid">
        <aside className="funnel-workbench-panel">
          <PanelHeader title="Version history" body="Newest first" />
          <div className="funnel-version-list">
            {workbench.versions.map((version) => (
              <a key={version.id} href={versionRoute(funnelId, version.id)} style={{
                ...versionRowStyle,
                background: version.id === selectedVersion?.id ? PALETTE.accentTint : PALETTE.surface,
                borderLeftColor: version.id === selectedVersion?.id ? PALETTE.accent : 'transparent',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                  <strong style={{ fontSize: 13, color: PALETTE.ink }}>Version {version.versionNumber}</strong>
                  <Pill tone={lifecycleTone(version.lifecycle)} style={{ fontSize: 9.5, padding: '3px 7px' }}>
                    {labelize(version.lifecycle)}
                  </Pill>
                </div>
                <div style={{ marginTop: 7, fontSize: 11, color: PALETTE.ink3 }}>
                  {new Date(version.createdAt).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </div>
              </a>
            ))}
          </div>
        </aside>

        <section className="funnel-workbench-panel funnel-preview-shell">
          <div style={previewToolbarStyle}>
            <div style={{ minWidth: 0 }}>
              <strong style={{ fontSize: 13 }}>Exact version preview</strong>
              <div style={{ fontSize: 11, color: PALETTE.ink3, marginTop: 3 }}>
                Version {selectedVersion?.versionNumber} / expires after 15 minutes
              </div>
            </div>
            <div style={{ flex: 1 }} />
            <div style={segmentStyle} aria-label="Preview viewport">
              {['desktop', 'mobile'].map((value) => (
                <button key={value} type="button" onClick={() => setViewport(value)} style={{
                  ...segmentButtonStyle,
                  background: viewport === value ? PALETTE.ink : 'transparent',
                  color: viewport === value ? '#fff' : PALETTE.ink2,
                }}>{labelize(value)}</button>
              ))}
            </div>
            {preview.url && (
              <Button variant="ghost" size="sm" onClick={() => window.open(preview.url, '_blank', 'noopener')}>Open preview</Button>
            )}
          </div>
          <div className={`funnel-preview-viewport ${viewport}`}>
            {preview.status === 'loading' ? (
              <ProtectedPageLoader title="Preparing preview" body={preview.message} minHeight={520} />
            ) : preview.status === 'error' ? (
              <Empty title="Preview unavailable" body={preview.message} />
            ) : preview.url ? (
              <>
                {preview.frameStatus === 'loading' && (
                  <ProtectedPageLoader title="Loading exact preview" body="Rendering the selected immutable funnel version." minHeight={520} />
                )}
                {preview.frameStatus === 'error' && (
                  <Empty title="Preview unavailable" body={preview.message} />
                )}
                <iframe
                  ref={previewFrameRef}
                  title={`Funnel version ${selectedVersion?.versionNumber} preview`}
                  src={previewEmbedUrl(preview.url, preview.channel)}
                  sandbox={previewSandbox}
                  className={preview.frameStatus === 'ready' ? '' : 'funnel-preview-frame-pending'}
                />
              </>
            ) : (
              <Empty title="No preview" body="This funnel does not have a ready version yet." />
            )}
          </div>
        </section>

        <aside className="funnel-workbench-panel funnel-workbench-inspector">
          <PanelHeader title="Version controls" body={canManage ? 'Changes create a new draft' : 'Read-only company access'} />
          <div className="funnel-inspector-body">
            <section style={inspectorSectionStyle}>
              <div style={factGridStyle}>
                <Fact label="Selected" value={`Version ${selectedVersion?.versionNumber || '-'}`} />
                <Fact label="Approval" value={labelize(selectedVersion?.approvalStatus)} />
                <Fact label="Staging" value={isSelectedPublished ? 'Live' : 'Not live'} />
                <Fact label="Production" value="Manual gate" />
              </div>
              {canManage && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
                  {selectedVersion?.approvalStatus !== 'approved' && (
                    <Button
                      variant="soft"
                      size="sm"
                      onClick={() => workbench.approvalRoute && navigate(workbench.approvalRoute)}
                      disabled={!workbench.approvalRoute || action.status === 'working'}
                    >Review &amp; approve</Button>
                  )}
                  {!isSelectedPublished && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={checkLinkIntegrity}
                        disabled={action.status === 'working'}
                      >Check links</Button>
                      <Button
                        variant="accent"
                        size="sm"
                        onClick={() => publishVersion()}
                        disabled={selectedVersion?.approvalStatus !== 'approved'
                          || workbench.linkIntegrity?.prePublish?.status === 'blocked'
                          || action.status === 'working'}
                      >Publish to staging</Button>
                      {workbench.linkIntegrity?.prePublish?.status === 'blocked' && (
                        <Button
                          variant="soft"
                          size="sm"
                          onClick={() => publishVersion({ override: true })}
                          disabled={selectedVersion?.approvalStatus !== 'approved' || action.status === 'working'}
                        >Publish with override</Button>
                      )}
                    </>
                  )}
                </div>
              )}
            </section>

            <section style={inspectorSectionStyle}>
              <div style={{ fontWeight: 650, marginBottom: 4 }}>Link &amp; booking readiness</div>
              <div style={{ fontSize: 12, lineHeight: 1.5, color: PALETTE.ink3, marginBottom: 12 }}>
                Broken external links block publication. A missing booking provider is a warning and stays unavailable until connected.
              </div>
              <LinkIntegritySummary label="Pre-publish" check={workbench.linkIntegrity?.prePublish} />
              {workbench.linkIntegrity?.postPublish && (
                <div style={{ marginTop: 12 }}>
                  <LinkIntegritySummary label="Published" check={workbench.linkIntegrity.postPublish} />
                </div>
              )}
            </section>

            <section style={inspectorSectionStyle}>
              <div style={{ fontWeight: 650, marginBottom: 4 }}>Content editor</div>
              <div style={{ fontSize: 12, lineHeight: 1.5, color: PALETTE.ink3, marginBottom: 12 }}>
                Edit approved copy and theme values. The published version remains unchanged until approval and publish.
              </div>
              {groups.length ? (
                <>
                  <label htmlFor="funnel-edit-group" style={fieldLabelStyle}>Section</label>
                  <select id="funnel-edit-group" value={editGroup} onChange={(event) => setEditGroup(event.target.value)} style={inputStyle}>
                    {groups.map((group) => <option key={group} value={group}>{group}</option>)}
                  </select>
                  <div style={{ display: 'grid', gap: 12, marginTop: 14 }}>
                    {visibleFields.map((field) => (
                      <label key={field.id} style={{ display: 'grid', gap: 6 }}>
                        <span style={fieldLabelStyle}>{field.label}</span>
                        {field.input === 'textarea' ? (
                          <textarea
                            value={draft[field.id] ?? ''}
                            onChange={(event) => setDraft((current) => ({ ...current, [field.id]: event.target.value }))}
                            disabled={!canManage}
                            rows={4}
                            style={{ ...inputStyle, resize: 'vertical' }}
                          />
                        ) : field.input === 'color' ? (
                          <div style={{ display: 'grid', gridTemplateColumns: '42px minmax(0, 1fr)', gap: 8 }}>
                            <input
                              type="color"
                              aria-label={`${field.label} swatch`}
                              value={draft[field.id] ?? '#000000'}
                              onChange={(event) => setDraft((current) => ({ ...current, [field.id]: event.target.value }))}
                              disabled={!canManage}
                              style={{ width: 42, height: 38, padding: 3, border: `1px solid ${PALETTE.line}`, borderRadius: 6, background: PALETTE.surface }}
                            />
                            <input
                              type="text"
                              value={draft[field.id] ?? ''}
                              onChange={(event) => setDraft((current) => ({ ...current, [field.id]: event.target.value }))}
                              disabled={!canManage}
                              style={inputStyle}
                            />
                          </div>
                        ) : (
                          <input
                            type={field.input}
                            value={draft[field.id] ?? ''}
                            onChange={(event) => setDraft((current) => ({ ...current, [field.id]: event.target.value }))}
                            disabled={!canManage}
                            style={inputStyle}
                          />
                        )}
                      </label>
                    ))}
                  </div>
                  {canManage && (
                    <Button
                      variant="primary"
                      onClick={saveVersion}
                      disabled={changes.length === 0 || action.status === 'working'}
                      style={{ marginTop: 16, width: '100%', justifyContent: 'center' }}
                    >Save new version ({changes.length})</Button>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 12, color: PALETTE.ink3 }}>This definition has no editable copy fields.</div>
              )}
            </section>

            <section style={inspectorSectionStyle}>
              <div style={{ fontWeight: 650, marginBottom: 10 }}>Publication</div>
              <div style={{ display: 'grid', gap: 9 }}>
                <Fact label="Staging revision" value={workbench.publications.staging?.revision || 'None'} />
                <Fact label="Live version" value={workbench.publications.staging?.versionId
                  ? `Version ${workbench.versions.find((item) => item.id === workbench.publications.staging.versionId)?.versionNumber || '-'}`
                  : 'Unpublished'} />
                <Fact label="Platform URL" value={workbench.activeUrl || 'Provisioned on first publish'} />
              </div>
            </section>
          </div>
        </aside>
      </div>
      <ActionFormDialog {...actionFormDialog.dialogProps} />
    </div>
  );
}

function PanelHeader({ title, body }) {
  return (
    <div style={{ padding: '14px 16px', borderBottom: `1px solid ${PALETTE.line}` }}>
      <div style={{ fontSize: 13, fontWeight: 650, color: PALETTE.ink }}>{title}</div>
      <div style={{ fontSize: 11, color: PALETTE.ink3, marginTop: 3 }}>{body}</div>
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={fieldLabelStyle}>{label}</div>
      <div style={{ fontSize: 12.5, color: PALETTE.ink, marginTop: 4, overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  );
}

function LinkIntegritySummary({ label, check }) {
  if (!check) {
    return (
      <div style={{ ...linkIntegrityStyle, color: PALETTE.ink3 }}>
        <strong style={{ color: PALETTE.ink }}>{label}</strong>
        <span style={{ fontSize: 11.5 }}>Not checked</span>
      </div>
    );
  }
  return (
    <div style={linkIntegrityStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
        <strong style={{ color: PALETTE.ink }}>{label}</strong>
        <Pill tone={integrityTone(check.status)}>{labelize(check.status)}</Pill>
      </div>
      <div style={{ marginTop: 7, fontSize: 11, color: PALETTE.ink3 }}>
        {check.blockerCount} blockers / {check.warningCount} warnings
        {check.checkedAt ? ` / ${new Date(check.checkedAt).toLocaleString('en-CA')}` : ''}
      </div>
      {(check.actions || []).map((item) => (
        <div key={`${item.kind}:${item.path}:${item.target}`} style={linkActionStyle}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11.5, fontWeight: 600, color: PALETTE.ink }}>{item.label}</div>
            <div style={{ fontSize: 10.5, color: PALETTE.ink3, overflowWrap: 'anywhere' }}>
              {item.target}
            </div>
          </div>
          <Pill tone={integrityTone(item.status)}>{labelize(item.reason)}</Pill>
        </div>
      ))}
    </div>
  );
}

function Notice({ tone, children }) {
  const error = tone === 'error';
  return (
    <div style={{
      padding: '11px 14px',
      marginBottom: 16,
      border: `1px solid ${error ? '#E8C9C1' : PALETTE.accentSoft}`,
      borderRadius: 8,
      background: error ? PALETTE.dangerSoft : PALETTE.accentTint,
      color: error ? '#8B2E1A' : PALETTE.ink2,
      fontSize: 12.5,
    }}>{children}</div>
  );
}

function Empty({ title, body }) {
  return (
    <div style={{ minHeight: 240, display: 'grid', placeItems: 'center', padding: 28, textAlign: 'center' }}>
      <div>
        <div style={{ fontWeight: 650, color: PALETTE.ink, marginBottom: 7 }}>{title}</div>
        <div style={{ maxWidth: 440, fontSize: 13, lineHeight: 1.55, color: PALETTE.ink3 }}>{body}</div>
      </div>
    </div>
  );
}

const eyebrowStyle = {
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: 10.5,
  letterSpacing: 0,
  textTransform: 'uppercase',
  color: PALETTE.accent,
  marginBottom: 7,
};
const pageTitleStyle = {
  margin: 0,
  fontFamily: 'Instrument Serif, serif',
  fontStyle: 'italic',
  fontSize: 48,
  fontWeight: 400,
  lineHeight: 1,
  letterSpacing: 0,
  color: PALETTE.ink,
};
const subtitleStyle = { margin: '10px 0 0', color: PALETTE.ink2, fontSize: 14, lineHeight: 1.5 };
const fieldLabelStyle = {
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: 9.5,
  letterSpacing: 0,
  textTransform: 'uppercase',
  color: PALETTE.ink3,
};
const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 11px',
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 6,
  background: PALETTE.surface,
  color: PALETTE.ink,
  fontFamily: 'Inter, sans-serif',
  fontSize: 13,
};
const funnelRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  gap: 16,
  alignItems: 'center',
  padding: '17px 8px',
  borderBottom: `1px solid ${PALETTE.line}`,
  color: 'inherit',
  textDecoration: 'none',
};
const versionRowStyle = {
  display: 'block',
  padding: '13px 14px 13px 12px',
  borderLeft: '3px solid transparent',
  borderBottom: `1px solid ${PALETTE.line}`,
  textDecoration: 'none',
};
const monoStyle = { fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: PALETTE.ink3 };
const backButtonStyle = {
  padding: 0,
  margin: '0 0 12px',
  border: 0,
  background: 'transparent',
  color: PALETTE.ink2,
  cursor: 'pointer',
  fontFamily: 'Inter, sans-serif',
  fontSize: 12,
};
const previewToolbarStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  flexWrap: 'wrap',
  padding: '11px 12px',
  borderBottom: `1px solid ${PALETTE.line}`,
};
const segmentStyle = {
  display: 'inline-flex',
  padding: 2,
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 7,
  background: PALETTE.surfaceAlt,
};
const segmentButtonStyle = {
  border: 0,
  borderRadius: 5,
  padding: '6px 9px',
  fontFamily: 'Inter, sans-serif',
  fontSize: 11,
  cursor: 'pointer',
};
const inspectorSectionStyle = { padding: 16, borderBottom: `1px solid ${PALETTE.line}` };
const factGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 };
const linkIntegrityStyle = {
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 6,
  padding: 10,
  background: PALETTE.surfaceAlt,
};
const linkActionStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  alignItems: 'center',
  gap: 8,
  paddingTop: 9,
  marginTop: 9,
  borderTop: `1px solid ${PALETTE.line}`,
};
